import { CaretDownIcon, CaretUpIcon, CheckIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Pressable, StyleProp, StyleSheet, View, ViewStyle, Text, Platform } from "react-native";

interface OptionItem {
  label: string;
  value: string;
}

interface CustomDropdownWebProps {
  options: OptionItem[];
  value: string | undefined;
  onChange: (value: string) => void;
  style?: StyleProp<ViewStyle>;
  error?: string;
  openUpward?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  placeholderTextColor?: string;
  accessibilityLabel?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dropdownStyle?: StyleProp<ViewStyle>;
  dropdownContainerStyle?: StyleProp<ViewStyle>;
  showArrow?: boolean;
  hideItemBorders?: boolean;
}

export const CustomDropdownWeb = ({
  value,
  options,
  onChange,
  style,
  error,
  openUpward = false,
  disabled = false,
  fullWidth = true,
  placeholder = "Select an Option",
  placeholderTextColor,
  accessibilityLabel,
  open: controlledOpen,
  onOpenChange,
  dropdownStyle,
  dropdownContainerStyle,
  showArrow = true,
  hideItemBorders = false,
}: CustomDropdownWebProps) => {
  // Initialize from the controlled prop so the dropdown is open immediately on the first
  // render when open={true} is passed, with no flash.
  const [isOpen, setIsOpen] = useState<boolean>(controlledOpen ?? false);
  const [isActive, setIsActive] = useState(false);
  const [hoveredOption, setHoveredOption] = useState(null);
  const dropdownRef = useRef(null);
  const isWeb = Platform.OS === "web";

  // Initialize the portal container synchronously so it's available on the first render,
  // including when open={true} is passed as an initial prop.
  const [portalContainer] = useState<HTMLDivElement | null>(() => {
    if (typeof document === "undefined") return null;
    const container = document.createElement("div");
    document.body.appendChild(container);
    return container;
  });

  // Sync the controlled prop into internal state whenever it changes so the parent
  // can programmatically open or close the dropdown.
  useEffect(() => {
    if (controlledOpen != null) {
      setIsOpen(controlledOpen);
    }
  }, [controlledOpen]);

  // Always use internal state as the source of truth. setIsOpen is always called so
  // user interactions (click-outside, select option) can close the dropdown even when
  // open={true} was passed — the parent is notified via onOpenChange to update its own state.
  const openState = isOpen;

  const setOpenState = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  // RN style types don't support overflowY/overflowX, but web does.
  // Casting through unknown keeps us type-safe without introducing `any`.
  const optionListWebOverflowStyle = isWeb
    ? ({ overflowY: "auto", overflowX: "hidden" } as unknown as ViewStyle)
    : null;

  const combinedStyles = [
    styles.dropdownContainer,
    fullWidth ? styles.fullWidth : styles.fitContent,
    error && error.length > 0 ? styles.dropdownContainerWithErrorSpace : null,
    openState ? styles.dropdownContainerOpen : null,
    style,
  ];

  const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

  const handleSelect = useCallback(
    (option: string) => {
      onChange(option);
      setOpenState(false);
      setIsActive(true);
    },
    [onChange, setOpenState]
  );

  // Write position directly to the portal container's DOM styles to bypass React Native's style system
  const positionPortalContainer = useCallback(() => {
    if (!isWeb || !portalContainer || !dropdownRef.current) return;
    const domNode = dropdownRef.current as unknown as HTMLElement;
    const rect = domNode.getBoundingClientRect();
    portalContainer.style.position = "fixed";
    portalContainer.style.zIndex = "10000";
    portalContainer.style.left = `${rect.left}px`;
    portalContainer.style.width = `${rect.width}px`;
    if (openUpward) {
      portalContainer.style.top = "";
      portalContainer.style.bottom = `${window.innerHeight - rect.top + 4}px`;
    } else {
      portalContainer.style.top = `${rect.bottom + 4}px`;
      portalContainer.style.bottom = "";
    }
  }, [isWeb, openUpward, portalContainer]);

  useLayoutEffect(() => {
    if (!openState) {
      if (portalContainer) portalContainer.style.cssText = "";
      return;
    }
    positionPortalContainer();

    let lastBottom = -Infinity;
    let stableFrames = 0;
    let rafId: number;
    const poll = () => {
      if (!dropdownRef.current) return;
      const bottom = (dropdownRef.current as unknown as HTMLElement).getBoundingClientRect().bottom;
      if (bottom !== lastBottom) {
        lastBottom = bottom;
        stableFrames = 0;
        positionPortalContainer();
      } else {
        stableFrames++;
      }
      if (stableFrames < 3) {
        rafId = requestAnimationFrame(poll);
      }
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [openState, positionPortalContainer, portalContainer]);

  // Cleanup the portal container on unmount.
  useEffect(() => {
    return () => {
      if (portalContainer && document.body.contains(portalContainer)) {
        document.body.removeChild(portalContainer);
      }
    };
  }, [portalContainer]);

  // Close the dropdown when the user scrolls or resizes the window (position would be stale).
  // Ignore scroll events that originate inside the portal (i.e. scrolling the option list itself).
  useEffect(() => {
    if (!isWeb || !openState) return;
    const handleScrollOrResize = (event?: Event) => {
      if (event && event.target instanceof Node && portalContainer?.contains(event.target)) {
        return;
      }
      positionPortalContainer();
      setOpenState(false);
    };
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isWeb, openState, portalContainer, positionPortalContainer]);

  // Click-outside handler checks both the trigger ref and the portal container.
  useEffect(() => {
    if (!isWeb) return;
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current && (dropdownRef.current as unknown as HTMLElement).contains(event.target);
      const inPortal = portalContainer?.contains(event.target);
      if (!inTrigger && !inPortal) {
        setOpenState(false);
        setIsActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isWeb, portalContainer, setOpenState]);

  const currentLabel = selectedOption ? selectedOption.label : placeholder;

  const buttonStyle = [
    styles.selectedValue,
    fullWidth ? styles.fullWidth : styles.fitContent,
    !fullWidth && styles.fillContainer,
    ...(disabled ? [styles.buttonDisabled] : []),
    ...(isActive ? (error != null && error !== "" ? [styles.buttonError] : [styles.buttonActive]) : []),
    dropdownStyle,
  ];

  const textStyle = selectedOption
    ? styles.textLabel
    : placeholderTextColor
      ? [styles.textPlaceholder, { color: placeholderTextColor }]
      : styles.textPlaceholder;

  const optionListElement = openState ? (
    <View style={[styles.optionList, optionListWebOverflowStyle, dropdownContainerStyle]} role="group">
      {options.map((option) => {
        const isSelected = value === option.value;
        const isHovered = hoveredOption === option.value;

        return (
          <Pressable
            accessibilityLabel={option.label}
            key={option.value}
            role="option"
            aria-selected={isSelected}
            onPress={() => {
              handleSelect(option.value);
            }}
            onHoverIn={() => setHoveredOption(option.value)}
            onHoverOut={() => setHoveredOption(null)}
            style={[
              styles.optionItem,
              hideItemBorders ? styles.optionItemNoBorder : null,
              isHovered ? styles.optionItemHover : null,
            ]}>
            {/* Check Icon (Position Absolute) */}
            {isSelected && (
              <View style={styles.checkIcon}>
                <CheckIcon size={16} style={{ height: 16, width: 16, color: "inherit" }} />
              </View>
            )}
            {/* Option Label */}
            <Text style={styles.textLabel}>{option.label}</Text>
          </Pressable>
        );
      })}
    </View>
  ) : null;

  return (
    <View style={combinedStyles} ref={dropdownRef}>
      <Pressable
        style={buttonStyle}
        onPress={() => {
          if (disabled) return;
          setOpenState(!openState);
        }}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel || placeholder}
        accessibilityRole="combobox"
        aria-expanded={openState}
        aria-haspopup="listbox"
        aria-label={accessibilityLabel || placeholder}>
        <Text style={textStyle}>{currentLabel}</Text>
        {showArrow &&
          (openState ? (
            <CaretUpIcon style={styles.caretIcon} size={16} />
          ) : (
            <CaretDownIcon style={styles.caretIcon} size={16} />
          ))}
      </Pressable>

      {/* Ghost Options to force width */}
      <View
        style={styles.ghostContainer}
        accessibilityElementsHidden={true}
        importantForAccessibility="no-hide-descendants">
        <View style={styles.selectedValue}>
          <Text style={styles.textLabel}>{placeholder}</Text>
          <CaretDownIcon style={styles.caretIcon} size={16} />
        </View>
        {options.map((option) => (
          <View key={option.value} style={styles.selectedValue}>
            <Text style={styles.textLabel}>{option.label}</Text>
            <CaretDownIcon style={styles.caretIcon} size={16} />
          </View>
        ))}
      </View>

      {error && error.length > 0 && <Text style={styles.errorText}>{error}</Text>}

      {/* Render options via portal on web to escape overflow-clipping ancestors. 
          The portal container itself is positioned via direct
          DOM style writes (positionPortalContainer). */}
      {isWeb
        ? portalContainer
          ? createPortal(optionListElement, portalContainer)
          : null
        : optionListElement}
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownContainer: {
    position: "relative",
    zIndex: 0,
    elevation: 0,
  },
  fullWidth: {
    width: "100%",
  },
  fitContent: {
    alignSelf: "flex-start",
  },
  fillContainer: {
    width: "100%",
  },
  dropdownContainerWithErrorSpace: {
    marginBottom: 18, // reserve space for error message
  },
  dropdownContainerOpen: {
    zIndex: 9999,
    elevation: 9999,
  },
  selectedValue: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    minHeight: 40,
  },
  buttonActive: {
    borderColor: "#4f46e5",
  },
  buttonDisabled: {
    backgroundColor: "#f3f4f6",
    borderColor: "#d1d5db",
  },
  buttonError: {
    borderColor: "#ef4444",
  },
  optionList: {
    padding: 4,
    backgroundColor: "white",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    maxHeight: 240,
    overflow: "hidden",
  },
  optionItem: {
    maxHeight: 400,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 10,
    paddingBottom: 10,
    paddingRight: 16,
    paddingLeft: 30,
    borderRadius: 6,
    position: "relative",
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
    width: "100%",
  },
  optionItemHover: {
    backgroundColor: "#f0f9ff",
  },
  optionItemNoBorder: {
    borderBottomWidth: 0,
  },
  checkIcon: {
    position: "absolute",
    left: 6,
    top: "50%",
    marginTop: -8,
    height: 16,
    width: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    position: "absolute",
    top: "100%",
    right: 0,
    left: 0,
    color: "#ef4444",
    fontSize: 12,
    marginTop: 4,
    marginLeft: 8,
  },
  caretIcon: {
    height: 16,
    width: 16,
    color: "#6b7280",
  },
  textLabel: {
    color: "#1f2937",
    fontWeight: "400",
    fontSize: 14,
  },
  textPlaceholder: {
    color: "#9ca3af",
    fontSize: 14,
  },
  ghostContainer: {
    height: 0,
    overflow: "hidden",
    opacity: 0,
  },
});
